// JOBE-0160.1: W1 KPI strip — six tiles.
// D10: every count is a link to a filtered drill target.
// Tone table per spec:
//   agentsActive     → ok
//   agentsDegraded   → warn if > 0, else ok
//   agentsDisabled   → bad if > 0, else neutral
//   proposalsOpen    → neutral
//   approvalsBlocked → warn if > 0, else neutral
//   tasksInFlight    → ok
//
// Data source — 2026-05-24:
//   When wcc.mock.kpi is ON, the fixture is rendered as-is (legacy demo).
//   When OFF (default), agent counts are derived from useOrganizationPeople()
//   (real /api/agents + /api/departments). The proposalsOpen / approvalsBlocked
//   / tasksInFlight tiles have no real endpoint yet and render '—' for the
//   value; the tile + label stay in place to preserve the W1 page layout.

import { useNavigate } from 'react-router-dom';
import { flaggedFetch } from '../lib/flags.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { KpiTile } from '../components/KpiTile/KpiTile.jsx';
import { useOrganizationPeople } from '../organization/fetchOrganizationPeople.js';
import './KpiRow.css';

const EM_DASH = '—';

export function KpiRow() {
  const navigate = useNavigate();
  const overrides = useFlagStore((s) => s.overrides);
  const mockKpi = flaggedFetch('wcc.mock.kpi', overrides);
  const { rows } = useOrganizationPeople();

  // Build the kpi shape. Mock-flag ON → return fixture untouched (legacy
  // demo behaviour). Mock-flag OFF → derive agent counts from real org
  // data; the three non-agent KPIs stay null (rendered as '—').
  let kpi;
  if (mockKpi) {
    kpi = mockKpi;
  } else {
    const agents = rows.filter((r) => r.type === 'agent');
    const agentsActive = agents.filter((a) => a.phase === 'Available').length;
    const agentsDegraded = agents.filter((a) => a.phase === 'Degraded').length;
    // Treat unknown/null phase as "not running" so the disabled tile reflects
    // the operator's real concern: how many agents aren't healthy?
    const agentsDisabled = agents.filter(
      (a) => a.phase === 'Disabled' || a.phase == null,
    ).length;
    kpi = {
      agentsActive,
      agentsDegraded,
      agentsDisabled,
      proposalsOpen: null,
      approvalsBlocked: null,
      tasksInFlight: null,
    };
  }

  const {
    agentsActive = 0,
    agentsDegraded = 0,
    agentsDisabled = 0,
    proposalsOpen = 0,
    approvalsBlocked = 0,
    tasksInFlight = 0,
  } = kpi;

  // Render '—' for null values (no real endpoint yet) while preserving
  // the tile + label.
  const fmt = (v) => (v == null ? EM_DASH : v);

  const tiles = [
    {
      label: 'Agents active',
      value: fmt(agentsActive),
      tone: 'ok',
      drill: '/organization?phase=available',
    },
    {
      label: 'Agents degraded',
      value: fmt(agentsDegraded),
      tone: agentsDegraded > 0 ? 'warn' : 'ok',
      drill: '/organization?phase=degraded',
    },
    {
      label: 'Agents disabled',
      value: fmt(agentsDisabled),
      tone: agentsDisabled > 0 ? 'bad' : 'neutral',
      drill: '/organization?phase=disabled',
    },
    {
      label: 'Proposals open',
      value: fmt(proposalsOpen),
      tone: 'neutral',
      drill: '/projects?status=proposed',
    },
    {
      label: 'Approvals blocked',
      value: fmt(approvalsBlocked),
      tone: approvalsBlocked > 0 ? 'warn' : 'neutral',
      drill: '/projects?status=approvals-blocked',
    },
    {
      label: 'Tasks in flight',
      value: fmt(tasksInFlight),
      tone: 'ok',
      drill: '/projects?status=in-flight',
    },
  ];

  return (
    <div className="kpi-row">
      {tiles.map((t) => (
        <KpiTile
          key={t.label}
          label={t.label}
          value={t.value}
          tone={t.tone !== 'neutral' ? t.tone : undefined}
          onClick={() => navigate(t.drill)}
        />
      ))}
    </div>
  );
}
